// src/modules/app-views/toolbox/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import ToolboxInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import toolboxInitActionButton from 'modules/app-views/toolbox/pages/Init/actions/actionButton';
import toolboxInitMenuButton from 'modules/app-views/toolbox/pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import ToolboxDocument from 'modules/app-views/toolbox/pages/Document';
import toolboxDocumentActionButton from 'modules/app-views/toolbox/pages/Document/actions/actionButton';
import toolboxDocumentMenuButton from 'modules/app-views/toolbox/pages/Document/actions/menuButton';
import toolboxDocumentDecorateRequest from './pages/Document/decorators/request';
import ToolboxAi from 'modules/app-views/toolbox/pages/Ai';
import aiGenerativeIcon from 'assets/images/ai-generative.png';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import ToolboxDocumentCardHeaderColumn1 from 'modules/app-views/toolbox/pages/Document/components/ToolboxDocumentCardHeaderColumn1';
import ToolboxDocumentCardHeaderColumn2 from 'modules/app-views/toolbox/pages/Document/components/ToolboxDocumentCardHeaderColumn2';
import ToolboxDocumentCardHeaderColumn3 from 'modules/app-views/toolbox/pages/Document/components/ToolboxDocumentCardHeaderColumn3';
import ToolboxParametersModal from 'modules/app-views/toolbox/pages/Init/modals/ToolboxParametersModal';
import seedToolboxParameters from 'modules/app-views/toolbox/seed';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'toolbox';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: ToolboxParametersModal,
  parametersSeed: seedToolboxParameters,

  // -------------------------------------------------------------------------
  // MIGRATORY
  // -------------------------------------------------------------------------
  migratory: {
    title: 'toolbox_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // -------------------------------------------------------------------------
    // PAGE CORE
    // -------------------------------------------------------------------------
    layout: 'list', // 'page' | 'list' | 'document'
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <ToolboxInit />
      </ListProvider>
    ),

    // -------------------------------------------------------------------------
    // TOUR
    // -------------------------------------------------------------------------
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // -------------------------------------------------------------------------
    // API & DATA
    // -------------------------------------------------------------------------
    api: {
      url: API.TOOLBOX.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // -------------------------------------------------------------------------
    // DECORATORS
    // -------------------------------------------------------------------------
    decorators: {
      request: null,
      responseDoc: null,
      responseData: null,
    },


    // -------------------------------------------------------------------------
    // ROUTING & NAVIGATION
    // -------------------------------------------------------------------------
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // -------------------------------------------------------------------------
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // -------------------------------------------------------------------------
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [
        { field: '_metadata.audit.updatedAt', order: 'desc', index: 1 },
      ],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // -------------------------------------------------------------------------
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // -------------------------------------------------------------------------
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['line', 'documentCode', 'createdAt'],
    },

    // -------------------------------------------------------------------------
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // -------------------------------------------------------------------------
    settings: null,

    // -------------------------------------------------------------------------
    // HEADER CONFIGURATION
    // -------------------------------------------------------------------------
    header: {
      visible: true,
      style: 'default', // 'default' | 'card'
      actions: ['filter', 'refresh', toolboxInitActionButton, toolboxInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },
  },

  ai: {
    highSecurity: false,

    // -------------------------------------------------------------------------
    // PAGE CORE
    // -------------------------------------------------------------------------
    layout: 'page',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props} hideBackToHomeLink={true}>
        <ToolboxAi />
      </DocumentProvider>
    ),

    // -------------------------------------------------------------------------
    // TOUR
    // -------------------------------------------------------------------------
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // -------------------------------------------------------------------------
    // API & DATA
    // -------------------------------------------------------------------------
    api: {
      url: null,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: false,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: false,
    },

    // -------------------------------------------------------------------------
    // DECORATORS
    // -------------------------------------------------------------------------
    decorators: {
      request: null,
      response: null,
    },

    // -------------------------------------------------------------------------
    // ROUTING & NAVIGATION
    // -------------------------------------------------------------------------
    routing: {
      endpoint: '/ai',
      paramKey: null,
      query: null,
    },

    // -------------------------------------------------------------------------
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // -------------------------------------------------------------------------
    pagination: null,

    // -------------------------------------------------------------------------
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // -------------------------------------------------------------------------
    table: null,

    // -------------------------------------------------------------------------
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // -------------------------------------------------------------------------
    settings: {},

    // -------------------------------------------------------------------------
    // HEADER CONFIGURATION
    // -------------------------------------------------------------------------
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        showHighlightTitle: 'new_tool',
        showHighlightIcon: aiGenerativeIcon,
      },

      card: {
        title: () => null,
        subtitle: () => null,
        column1: () => null,
        column2: () => null,
      },
    },
  },

  document: {
    highSecurity: false,

    // -------------------------------------------------------------------------
    // PAGE CORE
    // -------------------------------------------------------------------------
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <ToolboxDocument />
      </DocumentProvider>
    ),

    // -------------------------------------------------------------------------
    // TOUR
    // -------------------------------------------------------------------------
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // -------------------------------------------------------------------------
    // API & DATA
    // -------------------------------------------------------------------------
    api: {
      url: API.TOOLBOX.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // -------------------------------------------------------------------------
    // DECORATORS
    // -------------------------------------------------------------------------
    decorators: {
      request: toolboxDocumentDecorateRequest,
      response: null,
    },

    // -------------------------------------------------------------------------
    // ROUTING & NAVIGATION
    // -------------------------------------------------------------------------
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // -------------------------------------------------------------------------
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // -------------------------------------------------------------------------
    pagination: null,

    // -------------------------------------------------------------------------
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // -------------------------------------------------------------------------
    table: null,

    // -------------------------------------------------------------------------
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // -------------------------------------------------------------------------
    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: [
        'editRefresh',
        toolboxDocumentActionButton(),
        toolboxDocumentMenuButton(),
        'return',
      ],
      viewMode: 'auto',
    },

    // -------------------------------------------------------------------------
    // HEADER CONFIGURATION
    // -------------------------------------------------------------------------
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'

      avatar: {
        visible: false,
        placeholder: 'tool',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} name={props?.name} />,
        subtitle: props => <CardHeaderSubtitle {...props} fieldName="sku" />,

        column1: props => <ToolboxDocumentCardHeaderColumn1 {...props} />,
        column2: props => <ToolboxDocumentCardHeaderColumn2 {...props} />,
        column3: props => <ToolboxDocumentCardHeaderColumn3 {...props} />,

        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {
    name: 'Chave Pentalobe P2',
    sku: 'TBX001',
    brand: 'iFixit',
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
